/**
 * Postcoder API client — v2 address picker.
 *
 * Two endpoints exist:
 *   /pcw/autocomplete/find       — query string → suggestion list (PostCoderFind[])
 *   /pcw/autocomplete/retrieve   — id + query → full address (PostCoderRetrieve[])
 *
 * Both endpoints return JSON arrays on success. On API-level errors (bad key,
 * over-quota) Postcoder returns an opaque string blob — non-array — which the
 * legacy AddressLookup.tsx silently turned into a `TypeError` inside the IIFE.
 * This module narrows defensively before returning, and surfaces typed error
 * codes to the caller so the UI can render an explicit error state.
 *
 * The fetcher accepts an AbortSignal — callers MUST pass one (latest-wins).
 */
import type { Address } from './types';

export type PostCoderFind = {
  id: string;
  // Postcoder returns a small set of type values:
  //   "ADD"   — a final address suggestion (terminal — count is always 1)
  //   "STR"   — street with one or more buildings (count > 1 → drill down)
  //   "PCD"   — postcode with one or more addresses (count > 1 → drill down)
  //   "LOC"   — locality (drill down)
  //   "LAD"   — local-authority district (drill down)
  //   "CTY"   — county (drill down)
  //   "STATE" — state/region (drill down)
  // Coarse user queries (postcode prefix, street name) almost never return
  // ADD-typed rows directly; the picker has to drill via `pathfilter`.
  type: string;
  summaryline: string;
  count: number;
};

export type PostCoderRetrieve = {
  addressline1: string;
  addressline2: string;
  summaryline: string;
  subbuildingname: string;
  buildingname: string;
  number: string;
  premise: string;
  street: string;
  posttown: string;
  county: string;
  postcode: string;
};

export type PostcoderErrorCode =
  | 'MISSING_KEY'
  | 'NETWORK'
  | 'NON_OK'
  | 'BAD_PAYLOAD'
  | 'EMPTY';

export class PostcoderError extends Error {
  code: PostcoderErrorCode;
  constructor(code: PostcoderErrorCode, message?: string) {
    super(message ?? code);
    this.code = code;
    this.name = 'PostcoderError';
  }
}

const BASE_URL = 'https://ws.postcoder.com/pcw/autocomplete';

function readApiKey(): string {
  return process.env.REACT_APP_POSTCODER_API_KEY ?? '';
}

/**
 * Find suggestions for a partial address query.
 * Returns every suggestion Postcoder returns (STR / PCD / LOC / LAD / CTY /
 * STATE / ADD). The caller decides what to do with non-ADD rows — typically
 * a drill-down via `pathfilter` to narrow further.
 *
 * Pre-Phase-3 this function filtered to `type === 'ADD'` only — Postcoder's
 * find endpoint with `singlesummary=true` rarely returns ADD-typed rows for
 * coarse queries (e.g. `SW1` returns 30 STR/PCD rows, zero ADD), so the
 * filter dropped 100% of results and the picker showed an empty state. The
 * filter is gone; the drill-down pattern is implemented in the consumer.
 *
 * `pathfilter` (optional): when set, narrows the result set to rows under the
 * given suggestion id. Used to drill from a STR/PCD/LOC row that has count
 * > 1 down to its individual addresses.
 *
 * Caller MUST pass `signal`. The function throws `AbortError` if cancelled.
 */
export async function findAddresses(
  query: string,
  signal: AbortSignal,
  pathfilter?: string,
): Promise<PostCoderFind[]> {
  const apikey = readApiKey();
  if (!apikey) {
    throw new PostcoderError(
      'MISSING_KEY',
      'REACT_APP_POSTCODER_API_KEY not configured',
    );
  }
  const trimmed = query.trim();
  if (!trimmed) {
    return [];
  }

  let url = `${BASE_URL}/find?query=${encodeURIComponent(
    trimmed,
  )}&country=uk&singlesummary=true&apikey=${encodeURIComponent(apikey)}`;
  if (pathfilter) {
    url += `&pathfilter=${encodeURIComponent(pathfilter)}`;
  }

  let response: Response;
  try {
    response = await fetch(url, { signal });
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      throw err;
    }
    throw new PostcoderError('NETWORK', (err as Error).message);
  }

  if (!response.ok) {
    throw new PostcoderError('NON_OK', `HTTP ${response.status}`);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new PostcoderError('BAD_PAYLOAD', 'response was not JSON');
  }

  if (!Array.isArray(payload)) {
    throw new PostcoderError('BAD_PAYLOAD', 'response was not an array');
  }

  // Defensive: drop any row that isn't a well-formed object. Postcoder
  // occasionally returns sparse rows when an upstream record is incomplete.
  return (payload as PostCoderFind[]).filter(
    (suggestion) => suggestion && typeof suggestion.id === 'string',
  );
}

/**
 * Retrieve a full address by id (returned from `findAddresses`).
 * Postcoder's /retrieve endpoint returns a 1-element array — we shape that
 * into our flat `Address` so the rest of the SPA does not have to know about
 * the wire format.
 */
export async function retrieveAddress(
  id: string,
  query: string,
  signal: AbortSignal,
): Promise<Address> {
  const apikey = readApiKey();
  if (!apikey) {
    throw new PostcoderError(
      'MISSING_KEY',
      'REACT_APP_POSTCODER_API_KEY not configured',
    );
  }

  const url = `${BASE_URL}/retrieve/?id=${encodeURIComponent(
    id,
  )}&query=${encodeURIComponent(
    query,
  )}&country=uk&lines=2&apikey=${encodeURIComponent(apikey)}`;

  let response: Response;
  try {
    response = await fetch(url, { signal });
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      throw err;
    }
    throw new PostcoderError('NETWORK', (err as Error).message);
  }

  if (!response.ok) {
    throw new PostcoderError('NON_OK', `HTTP ${response.status}`);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new PostcoderError('BAD_PAYLOAD', 'response was not JSON');
  }

  if (!Array.isArray(payload) || payload.length === 0) {
    throw new PostcoderError('EMPTY', 'no address returned for id');
  }

  const result = payload[0] as PostCoderRetrieve;

  return {
    line1: result.addressline1 ?? '',
    line2: result.addressline2 ?? '',
    city: result.posttown ?? '',
    county: result.county ?? '',
    postCode: result.postcode ?? '',
    country: 'United Kingdom',
  };
}
